import React, { memo, useEffect, useState, CSSProperties, MutableRefObject, useRef, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, icons } from "../../Styles";
import { ProgressCircle, FieldMode, LineInfoDisplay, UNIT } from "..";
import { SelectFieldOption_Values } from "../../Definitions";
import { UndefinedOptions, useFeedcurveOptions } from "../_Components";
import { AnimalInfo, FeedCurve, MaintainAnimal, MilestoneInfo, useSkipFeedTime } from "../../Services";
import store, { useAppDispatch, useAppSelector } from "../../Store";
import { selectActiveSectionById } from "../../Store/activeStateHandlers/activeSectionSlice";
import { selectActiveAnimalById, updateActiveAnimal } from "../../Store/activeStateHandlers/activeAnimalSlice";
import { selectFeedCurveById } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice";
import { Dispatch, UnknownAction } from "redux";
import { debounce } from "lodash";


// #region | Progress Circles
export const ProgressCircles = memo(({ consumptionTotalToday, consumptionProgressedToday, consumptionTotalYesterday, consumptionProgressedYesterday }: {
  consumptionTotalToday: number,
  consumptionProgressedToday: number,
  consumptionTotalYesterday: number,
  consumptionProgressedYesterday: number
}) => {
  const { t } = useTranslation();

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "start", gap: "30px", height: "220px", flexShrink: "0", width: "100%" }}>
      <ProgressCircle
        focusView
        animation
        header={`${t("feed.general.remainingToday", {
          defaultValue: "remaining today",
        })}`}
        total={consumptionTotalToday}
        progressed={consumptionProgressedToday}
        unit={<UNIT focusView content={"KG"} color={colors.greyPrimary} glyph={glyphs.Kg({fillColor: colors.greyPrimary})}/>}
        tooMuchFedDisclaimer
      />
      <ProgressCircle
        focusView
        animation
        header={`${t("feed.general.remainingYesterday", {
          defaultValue: "remaining yesterday",
        })}`}
        total={consumptionTotalYesterday}
        progressed={consumptionProgressedYesterday}
        unit={<UNIT focusView content={"KG"} color={colors.greyPrimary} glyph={glyphs.Kg({fillColor: colors.greyPrimary})}/>}
        tooMuchFedDisclaimer
      />
    </div>
  )
})
// #endregion

function mutateValueIntoRef(dirtRef: MutableRefObject<MaintainAnimal[]>, key: keyof MaintainAnimal, value: boolean | number, animalId: number) {
  if (dirtRef.current === undefined) {
    dirtRef.current = [];
  }

  const animalInRef = dirtRef.current.find((animal) => animal.animal_id === animalId);
  if (animalInRef) {
     Object.assign(animalInRef, {[key]: value});
  }
  dirtRef.current.push({animal_id: animalId, [key]: value})
} 

const debounceAnimalUpdate = debounce((key: keyof AnimalInfo, value: number, animal: AnimalInfo, dispatch: Dispatch<UnknownAction>) => {
  dispatch(updateActiveAnimal({id: animal.animal_id, changes: {...animal, [key]: value }}));
}, 500)

// #region | Line Info Displays
export const LineInfoDisplays = memo(({ domain, locationId, animalAtLocation, editMode, feedCurve, feedCorrection, milestone, milestone_day, feedcurve_milestones, dirtRef, feedSkipHandler }: {
  animalAtLocation: AnimalInfo,
  locationId: number,
  editMode: boolean,
  feedCurve?: FeedCurve,
  feedCorrection: string,
  milestone?: Omit<MilestoneInfo, "deleted">,
  milestone_day?: number,
  feedcurve_milestones?: number,
  dirtRef: MutableRefObject<MaintainAnimal[]>,
  domain: string,
  feedSkipHandler: {
    feedSkipTime: Map<number, number>;
    setFeedSkipTime: React.Dispatch<SetStateAction<Map<number, number>>>;
    feedSkipSuccess: MutableRefObject<{[locationId: number]: boolean}>;
  }
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
    
  const { feedcurveOptions, milestoneOptionsFromFeedcurve } = useFeedcurveOptions(UndefinedOptions.nan, animalAtLocation.feedcurve_id ?? 0)
  const [ feed, setFeed ] = useState<number | undefined>(animalAtLocation?.correction);
  useEffect(() => {
    setFeed(animalAtLocation?.correction)
  }, [animalAtLocation?.correction]);

  // Feed Skip
  const {feedSkipTime, setFeedSkipTime, feedSkipSuccess} = feedSkipHandler;
  let feedSkipValue = <></>;
  if (feedSkipSuccess.current[locationId] !== undefined) {
    feedSkipValue = feedSkipSuccess.current[locationId] ? <glyphs.Check fillColor={colors.systemGreen} size={"21px"} /> : <glyphs.Empty fillColor={colors.systemRed} size={"21px"} />
  }

  return (<>
    {/* Feed Correction */}
    <LineInfoDisplay
      key={locationId}
      editMode={editMode}
      focusView
      gridTemplateColumns={"7fr 25px 8fr"}
      value={feedSkipValue}
      header={t("locationFeederPages.locationFeeder.feedSkip", { defaultValue: "Feed Skip" })}
      icon={"FeedPlan"}
      fieldMode={FieldMode.input}
      type="number"
      onchange={
        (e) => setFeedSkipTime(old => {old.set(locationId, Number(e)); return old})
      }
      defaultValue={feedSkipTime.get(locationId)}
    />

    {/* Feed Curve */}
    <LineInfoDisplay
      editMode={editMode}
      focusView
      gridTemplateColumns={"7fr 25px 8fr"}
      value={feedCurve?.name}
      undefinedValue={t("negativeParams.feedCurve", { defaultValue: "No Feed Curve" })}
      header={t("feed.feedCurve.feedCurve", { defaultValue: "Feed Curve" })}
      icon={"FeedCurve"}
      fieldMode={FieldMode.select}
      options={feedcurveOptions ?? []}
      onchange={(option: SelectFieldOption_Values | undefined) => {
     
        mutateValueIntoRef(dirtRef, "feedcurve_id", Number(option?.id), animalAtLocation.animal_id);
        if (isNaN(Number(option?.id))) {
          dispatch(updateActiveAnimal({id: animalAtLocation.animal_id, changes: {...animalAtLocation, feedcurve_id: Number(option?.id)}}))
          return;
        }

        const feedCurve = selectFeedCurveById(store.getState().graphQlData.feedCurves, Number(option?.id)) ?? undefined;
        if (feedCurve === undefined) {
          dispatch(updateActiveAnimal({id: animalAtLocation.animal_id, changes: {...animalAtLocation, feedcurve_id: Number(option?.id)}}))
          return;
        }
         
        const milestone = feedCurve.milestones?.find((milestone) => milestone.milestoneId === animalAtLocation.milestone?.milestone_id);
        if (milestone !== undefined) {
          dispatch(updateActiveAnimal({id: animalAtLocation.animal_id, changes: {...animalAtLocation, feedcurve_id: Number(option?.id), feedcurve_milestones: milestone.feedCurveMilestoneId}}))
          mutateValueIntoRef(dirtRef, "fk_feedcurve_milestones", milestone.feedCurveMilestoneId, animalAtLocation.animal_id);
          if (animalAtLocation.milestone_day) {
            mutateValueIntoRef(dirtRef, "milestone_day", Math.floor(animalAtLocation.milestone_day/1000), animalAtLocation.animal_id)
          }
          return;
        }

        const newMilestone = feedCurve.milestones?.at(0);
        dispatch(updateActiveAnimal({id: animalAtLocation.animal_id, changes: {...animalAtLocation, feedcurve_id: Number(option?.id), feedcurve_milestones: newMilestone?.feedCurveMilestoneId, milestone: newMilestone ? {milestone_id: newMilestone.milestoneId, name: newMilestone.name} : newMilestone, milestone_day: Date.now()}}))
        mutateValueIntoRef(dirtRef, "fk_feedcurve_milestones", newMilestone ? newMilestone.feedCurveMilestoneId : NaN, animalAtLocation.animal_id);
        mutateValueIntoRef(dirtRef, "milestone_day", Math.floor(Date.now()/1000), animalAtLocation.animal_id);
      }}
      defaultValue={feedcurveOptions.find(({ id }) => feedCurve?.feedCurveId === id) ?? undefined}
      isClearable
    />
    {/* Feed Correction */}
    <LineInfoDisplay
      editMode={editMode}
      focusView
      gridTemplateColumns={"7fr 25px 8fr"}
      value={feedCorrection}
      header={t("feed.feedCorrection.feedCorrection", { defaultValue: "Feed Corrections" })}
      icon={"FeedCorrection"}
      fieldMode={FieldMode.feedCorrection}
      onChange={(e: number) => {mutateValueIntoRef(dirtRef, "correction", Number(e), animalAtLocation.animal_id); setFeed(e); debounceAnimalUpdate("correction", Number(e), animalAtLocation, dispatch) }}
      defaultValue={feed}
    />
    {/* Milestone Name */}
    <LineInfoDisplay
      editMode={editMode}
      focusView
      gridTemplateColumns={"7fr 25px 8fr"}
      value={milestone?.name ?? ""}
      header={t("feed.feedCorrection.milestone", { defaultValue: "Milestone" })}
      undefinedValue={t("negativeParams.milestone", { defaultValue: "No Milestone" })}
      icon={"Milestone"}
      fieldMode={FieldMode.select}
      options={milestoneOptionsFromFeedcurve ?? []} // TODO
      onchange={(option: SelectFieldOption_Values | undefined) => {
        
        mutateValueIntoRef(dirtRef, "fk_feedcurve_milestones", Number(option?.id), animalAtLocation.animal_id);
        dispatch(updateActiveAnimal({id: animalAtLocation.animal_id, changes: {...animalAtLocation, feedcurve_milestones: Number(option?.id)}}))
       
      }}
      defaultValue={milestoneOptionsFromFeedcurve?.find(({id}) => animalAtLocation&&animalAtLocation.feedcurve_milestones === id) ?? undefined}
      isClearable
    />
    {/* Milestone Day */}
    <LineInfoDisplay 
      editMode={editMode}
      focusView
      gridTemplateColumns={"7fr 25px 8fr"}
      value={milestone_day ? Math.floor((Date.now() - milestone_day) / (1000 * 60 * 60 * 24)) : undefined}
      header={t("params.milestoneDay", { defaultValue: "Milestone Day" })}
      undefinedValue={t("negativeParams.milestoneDay", { defaultValue: "No Milestone Day" })}
      icon={"MilestoneDay"}
      fieldMode={FieldMode.input}
      onBlur={() => {
        const animal = dirtRef.current.find((animal) => animal.animal_id === animalAtLocation.animal_id);
        if (animal && animal.milestone_day && animal.milestone_day !== animalAtLocation.milestone_day) {
          dispatch(updateActiveAnimal({id: animal.animal_id, changes: {...animal, milestone_day: animal.milestone_day*1000}}))}
        }
      }
      onchange={(e: string|number) =>{mutateValueIntoRef(dirtRef,"milestone_day", Math.round((Date.now() - Number(e) * 86400000) / 1000), animalAtLocation.animal_id );}}
      defaultValue={milestone_day ? Math.floor((Date.now() - milestone_day) / (1000 * 60 * 60 * 24)) : undefined}
      isClearable
      type='number'
    />
  </>)
})
// #endregion

// Seperator
const seperator = (<div style={{width: "80%", minWidth: "280px", height: "2px", backgroundColor: colors.greyTertiary, flexShrink: "0",}}/>);

type Fw_locationFeederProps = {
  location: number
  editMode: boolean
  dirtyDataRef: MutableRefObject<MaintainAnimal[]>
  domain: string
  feedSkipHandler: {
    feedSkipTime: Map<number, number>;
    setFeedSkipTime: React.Dispatch<SetStateAction<Map<number, number>>>;
    feedSkipSuccess: MutableRefObject<{[locationId: number]: boolean}>;
  }
}
/**
 * FwLocationFeeder
 * @param location - Location Number to get animal from
 * @param editMode - Boolean to set LineInfoDisplays to editMode
 * @returns A view with animal info for animal at specifik location with Freeda Location Feeder 
 */
export default function FvLocationFeeder({ location, editMode, dirtyDataRef, domain, feedSkipHandler }: Fw_locationFeederProps) {
  const { t } = useTranslation();

  // #region | Animal Info
  const activeSection = useAppSelector((state) => selectActiveSectionById(state.activeState.section, location));
  const animalInSection = activeSection?.animals.at(0);
  
 const animalAtLocation = useAppSelector((state) => {if (animalInSection?.animal_id) {return selectActiveAnimalById(state.activeState.activeAnimal, animalInSection.animal_id)}});
  // getLocationAnimalJoining

  const feedCurve = useAppSelector((state) => {if (animalAtLocation?.feedcurve_id) {return selectFeedCurveById(state.graphQlData.feedCurves, animalAtLocation.feedcurve_id)}} );
  
  if (animalAtLocation === undefined || animalAtLocation === undefined) {
    return <div style={{display: "flex", alignItems: "center", justifyContent: "center", ...css.emptyPen, ...fonts.fontSize3, opacity: "0.5"}}>{t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty Pen"})}</div>;   
  }
  const animalNumber = animalAtLocation.animal_number

  // Consumption today and yesterday
  const consumptionToday = animalAtLocation.consumptions ? animalAtLocation.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 0):undefined;
  const consumptionTotalToday = +((consumptionToday?.total ?? 0) / 1000).toFixed(2);
  const consumptionProgressedToday = +(consumptionTotalToday - (consumptionToday?.progressed ?? 0) / 1000).toFixed(2);
  const consumptionYesterday = animalAtLocation.consumptions ? animalAtLocation.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 1):undefined;
  const consumptionTotalYesterday = +((consumptionYesterday?.total ?? 0) / 1000).toFixed(2);
  const consumptionProgressedYesterday = +(consumptionTotalYesterday - (consumptionYesterday?.progressed ?? 0) / 1000).toFixed(2);
  
  // Feed Curve

  // Feed Correction
  const feedCorrection = animalAtLocation.correction === undefined || animalAtLocation.correction === null
  ? "0%"
  : `${animalAtLocation.correction}%`;
  
  // Milestone
  const milestone = animalAtLocation.milestone
  const milestone_day = animalAtLocation.milestone_day
  const feedcurve_milestones = animalAtLocation.feedcurve_milestones
  const animalInfo = {
    animalNumber,
    consumptionTotalToday,
    consumptionTotalYesterday,
    consumptionProgressedToday,
    consumptionProgressedYesterday,
    feedCorrection,
    feedCurve,
    milestone,
    milestone_day,
    feedcurve_milestones
  }
  // #endregion
  
  // Fallback if no animal at location
  if (!animalInfo) return <div style={{display: "flex", alignItems: "center", justifyContent: "center", ...css.emptyPen, ...fonts.fontSize3, opacity: "0.5"}}>{t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty Pen"})}</div>;

  // Styling
  const areas: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    flexShrink: "0",
    height: "100%",
    boxSizing: "border-box"
  }

  return ( <>
    {/* <div style={{ gridArea: "2/1/4/2", display: "grid", flexDirection: "column", alignItems: "center", justifyContent: "start", boxSizing: "border-box", overflowY: "auto", height: "100%", scrollbarColor: colors.greyPrimary+colors.transparent, scrollbarWidth: "thin", gridTemplateRows: "352px 1fr", gridTemplateColumns: "1fr" }}> */}
    
      {/* ---------- WHITE AREA ---------- */}
      <div style={{ gridArea: "1/1/2/2", backgroundColor: colors.greyLightTertiary, gap: "20px", ...areas }}>
        
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "5px", height: "50px", flexShrink: "0", }}>
          <img src={icons.animalsColor} style={{ width: "50px" }} alt="icon"/>
          <div>
            <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyPrimary }}> {t(`focus_viewPages.inThisPen`, { defaultValue: "in this pen"})} </p>
            <p style={{ ...fonts.header, ...fonts.fontSize2, color: colors.greyDarkPrimary }}>
              {`${t("animalPages.animal.animal", { defaultValue: "animal" })} ${animalInfo.animalNumber}`}
            </p>  
          </div>
        </div>

        {seperator}
        
        <ProgressCircles
          consumptionProgressedToday={animalInfo.consumptionProgressedToday}
          consumptionProgressedYesterday={animalInfo.consumptionProgressedYesterday}
          consumptionTotalToday={animalInfo.consumptionTotalToday}
          consumptionTotalYesterday={animalInfo.consumptionTotalYesterday}
        />
      </div>

      {/* ---------- GREY AREA ---------- */}
      <div style={{ gridArea: "2/1/3/2", backgroundColor: colors.greyPrimary, gap: "10px", ...areas}}>
      <LineInfoDisplays
          locationId={location}
          animalAtLocation={animalAtLocation}
          editMode={editMode}
          feedCurve={animalInfo.feedCurve}
          feedCorrection={animalInfo.feedCorrection}
          milestone={animalInfo.milestone}
          milestone_day={animalInfo.milestone_day}
          feedcurve_milestones={animalInfo.feedcurve_milestones}
          dirtRef={dirtyDataRef}
          domain={domain}
          feedSkipHandler={feedSkipHandler}
        />
      </div>

    {/* </div> */}
 </> )
}